import { IEmailRecipient } from '@nx-fe/services';
import * as quill from 'quill';
/* eslint-disable prefer-arrow/prefer-arrow-functions */
export const UNMANAGED_DEVICE_STRING = 'Unmanaged';

export interface Activity {
  activityId: number;
  activityTypeId: number;
  createdBy: string;
  creationDate: string;
  description: string;
  details: any; // This depends on activity type
  updateDate: string;
  updatedBy: string;

  // from IActivityModel:
  // activityId: number;
  // activityType: string;
  creationUserId: string;
  creationUserPublicId?: string; // This should be removed - the Id _is_ the public id
  updateUserId?: number;
  updateUserPublicId?: string; // This should be removed - the Id _is_ the public id
  parentActivityId?: number;
  childActivities: Activity[];
  isDirty: () => boolean;
  getCustomIconClass?: () => string;
  $repeaterId?: string;
  $$creationUserInfo?: UserInfo;
  $$updateUserInfo?: UserInfo;
}

export interface UserInfo {
  userId: string;
  userName: string;
}

export interface UserDetails extends UserInfo {
  connectionName: string;
  displayName: string;
  userGroups: string[];
  // domain: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  // isEditable?: boolean;

  // ReporterModel
  // reporterPublicId: string;
  // fullName?: string;

  // sid: string;
  // userType: string;
}

export type ReporterInfo = UserInfo;

export interface WatcherInfo extends UserInfo {
  elementId: string;
  elementType: string;
}

export interface WatcherDetails extends WatcherInfo, UserDetails {
  role: string;
}

export interface Tag {
  tagId: number;
  tag: string;
  tagGroupId?: number;
}

export interface TagGroup {
  groupId: number;
  groupName: string;
  color?: string;
}

export interface LinkedIncident {
  ticketId: number;
  prefixedId: string;
  subject: string;
  status: string;
  priority: string;
  linkTypeId: number;
  isDeleted: boolean;
  description?: string;
}

export interface Ticket {
  activities: Activity[];
  assigneeId?: string;
  assigneeName?: string;
  creationDate: string;
  creationUserId?: string;
  description: string;
  devices: any[]; // TODO: type
  dueDate: string;
  groupId: number;
  groupName: string;
  isAssigneeSubscribed: boolean;
  isDeleted: boolean;
  isReporterSubscribed: boolean;
  linkedIncidents?: LinkedIncident[];
  priority: string;
  reportedMethod: string;
  reporterId?: string;
  reporterName: string;
  resolutionDate?: string;
  resolutionTargetDate?: string;
  resolutionUserId?: string;
  shortGroupName: string;
  status: string;
  submissionDate?: string;
  tags: Tag[];
  ticketId: number;
  title: string;
  updateDate: string;
  updateUserId: string;
  watchers?: UserInfo[];

  /** Allowable Actions. $$ strips this field from being serialized by JSON.stringify */
  // $$allowableActions: string[];
}

export interface TicketActivity extends Activity {
  /** The ticket id */
  ticketId: string;
}

export interface AttachmentActivity extends Activity {
  /** The file Id */
  fileId: string;
  /** The original file name of the attachment */
  fileName: string;
  /** The file size, in bytes */
  fileSize: number;
  /** The friendly name for the attachment */
  displayName?: string;
  /** Base64 png encoded string for thumbnail */
  thumbnail?: string;
  /** The mcInstance Name that shows where is this attachment from */
  $mcInstanceName: string;
  /** Client-side file object for upload ($$ strips property from JSON serialization) */
  $$file: File;
  /** Whether the attachment is local only */
  $$isLocal: boolean;
  /** The preview id for streaming */
  $$previewId: string;
  /**
   * Callback used to notify about attachment upload completion/failure.
   *
   * @param error Error object if there was an upload failure, otherwise undefined.
   */
  onUploadComplete: (result: UploadCompleteReason) => void;
}

/** Enumeration of upload complete reasons */
export const enum UploadCompleteReason {
  /** The upload was successful */
  Success,
  /** The upload was cancelled by the user */
  Cancelled,
  /** There was an error during the upload */
  Error,
}

export interface ActivityDisplayInformation extends Activity {
  activityTypeCaption: string;
  activityProcurementCaption: string;
  activityCaption: string;
  activityInformation: string[];
  activityThumbnail: string;
}

export interface RemoteControlActivity extends Activity {
  details: {
    deviceId: string;
    terminationDate: string;
  };
  $$error: string;
}

export interface NoteActivity extends Activity {
  details: {
    isPrivate: boolean;
    isResolution: boolean;
  };
}

export interface UploadedFile {
  fileId?: string;
  fileName?: string;
  fileSize?: number;
  displayName?: string;
}

export interface DeviceCaptureActivity extends Activity {
  details: {
    deviceId: string;
    deviceName: string;
    mcConnectionId: number;
    mcConnectionName: string;
    isDefaultConnection: boolean;
    status: number;
    captureType: number;
    deviceFamily: string;
    captureDescription: string;
    files?: UploadedFile[];
    /** The device capture files are still been uploaded, not ready for download **/
    $$isDraft: boolean;
    /** The preview id for streaming */
    $$previewId: string;
  };
}

export interface SnapshotActivity extends Activity {
  details: {
    deviceId: string;
    deviceName: string;
    snapshotDate: string;
    mcDeviceId: string;
    mcConnectionId: number;
    mcConnectionName: string;
    isDefaultConnection: boolean;
    family: string;
    isSaved: boolean;
    data: any; // sections
  };
}

/** Enumeration for support email address validation code */
const enum EmailAddressValidationCode {
  /** Email address is valid</summary> */
  Valid = 0,
  /** <summary>Email address is invalid</summary> */
  InvalidFormat = 1,
  /** <summary>Used in group configuration</summary> */
  UsedInGroupConfiguration = 2,
}

// This could either be an address or a user. Users addresses are retrieved via userDetail
export interface EmailRecipient {
  address?: string;
  name: string;
  recipientType: string;
  userId?: string;
  errorValidationCode?: EmailAddressValidationCode;
  isInvalidAddress?: boolean;
}

export interface EmailRecipientWithUser extends EmailRecipient {
  userDetail: UserDetails;
}

export function isEmailRecipientWithUser(recipient: EmailRecipient): recipient is EmailRecipientWithUser {
  return recipient.userId !== undefined;
}

export interface Email {
  /** The activity ID of the email */
  activityId?: number;

  /** The email message */
  body: string;

  /** The subject */
  subject: string;

  /** The email Recipients */
  recipients: EmailRecipient[];

  /** The activity IDs of the attachments to include (or that are included) */
  attachmentActivityIds?: number[];
}

/** hold values when user select an outbound email attachment */
export interface EmailAttachment {
  /** when user selects a new file from local drive */
  fileObj: { file: File; invalidFiles: File[] };

  /** when the user selects an attachment already uploaded to the activity */
  attachment: AttachmentActivity;
}

export type AttachmentType = 'file' | 'activity';

export interface EmailContainer {
  activityId: number;
  ticketId: number;
  recipients: IEmailRecipient[];
  body: any;
  attachmentActivityIds: EmailAttachmentFile[];
  subject: string;
}

export interface EmailAttachmentFile {
  fileId: string;
  fileName: string;
  fileSize?: number;
  displayName?: string;
}

export interface EmailActivity extends Activity {
  activityType: string;
  body: string;
  status: string;
}

export interface InboundEmailActivity extends Activity {
  details: {
    body: string;
    isHtml: boolean;
    sender: string;
    senderEmail: string;
    status: string;
    subject: string;
    files: UploadedFile[];
  };
  // body: string;
  // recipientIds: string[];
  // recipientCCIds: string[];
  // isHtml: boolean;
}

export interface FileInfo {
  attachmentId: string;
  fileName: string;
}

export enum ActivityTypeEnum {
  note = 1,
  attachment = 2,
  inboundEmail = 3,
  outboundEmail = 4,
  remoteControl = 5,
  deviceLog = 6,
  deviceSnapshot = 7,
  deviceVideo = 8,
  deviceScreenshot = 9,
}

export enum TicketStatusEnum {
  draft = 1,
  open = 2,
  pending = 3,
  resolved = 4,
}

export function isNoteActivity(activity: Activity): activity is NoteActivity {
  return activity.activityTypeId === ActivityTypeEnum.note;
}

export function isAttachmentActivity(activity: Activity): activity is AttachmentActivity {
  return activity.activityTypeId === ActivityTypeEnum.attachment;
}

export function isInboundEmailActivity(activity: Activity): activity is InboundEmailActivity {
  return activity.activityTypeId === ActivityTypeEnum.inboundEmail;
}

export function isOutboundEmailActivity(activity: Activity): activity is EmailActivity {
  return activity.activityTypeId === ActivityTypeEnum.outboundEmail;
}

export function isRemoteControlActivity(activity: Activity): activity is RemoteControlActivity {
  return activity.activityTypeId === ActivityTypeEnum.remoteControl;
}

export function isDeviceCaptureActivity(activity: Activity): activity is DeviceCaptureActivity {
  return (
    activity &&
    (activity.activityTypeId === ActivityTypeEnum.deviceLog ||
      activity.activityTypeId === ActivityTypeEnum.deviceVideo ||
      activity.activityTypeId === ActivityTypeEnum.deviceScreenshot)
  );
}

export function isDeviceSnapshotActivity(activity: Activity): activity is SnapshotActivity {
  return activity.activityTypeId === ActivityTypeEnum.deviceSnapshot;
}
